import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { takeUntil } from "rxjs/operators";

import { BaseComponent, ContentService, EventsService, UserService, Video, environment } from "@mypxplat/xplat/core";
import { AppService, SeoService } from "@mypxplat/xplat/web/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-guest-home",
  templateUrl: "guest-home.component.html",
  standalone: false,
})
export class GuestHomeComponent extends BaseComponent implements OnInit {
  public env = environment;
  public randomBackgroundImage: string;
  public videos: Video[] = [];
  public videosLoading: boolean = true;
  public newEvents: any[] = [];
  public newEventsLoading: boolean = true;

  constructor(
    userService: UserService,
    public appService: AppService,
    public contentService: ContentService,
    public eventsService: EventsService,
    private _router: Router,
    private _seo: SeoService
  ) {
    super(userService);
  }

  ngOnInit(): void {
    this.appService.setPageTitle("Home");
    this._seo.setPageMeta({
      title: "MyFender — Your home for gear, learning, community, and events",
      description: "Register your Fender and PreSonus gear, watch guitar lessons and studio tutorials, join the musician community, and discover livestreams and events.",
      canonical: "https://my.fender.com/home",
      ogImage: "https://my.fender.com/assets/images/hero/fui-hero10.jpg",
      ogType: "website",
    });
    this.randomBackgroundImage = this._pickRandomBackgroundImage();

    this.contentService.educationDataStream$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (!result) return;
      this.videosLoading = false;
      const availableVideos = [...result.videos].sort((a, b) => (a.created > b.created ? -1 : 1));
      this.videos = availableVideos.slice(0, 4);
    });
    if (!this.contentService.educationDataStream) {
      this.contentService.getEducationData(false, true).subscribe({ error: () => (this.videosLoading = false) });
    }

    this._loadNewEvents();
  }

  signIn() {
    this.appService.redirectToConnect(true, false);
  }

  goToEvent(event: any) {
    this._router.navigate(["events", event.slug || event.id]);
  }

  upgrade() {
    this.appService.redirectToConnect(true, false);
  }

  private _loadNewEvents() {
    if (this.eventsService.cachedPublicEvents.length) {
      this.newEvents = this.eventsService.cachedPublicEvents
        .map((e) => this._enrichEvent(e))
        .filter((e) => !this._endedBeforeToday(e))
        .slice(0, 5);
      this.newEventsLoading = false;
      return;
    }
    this.eventsService.getPublicEvents(50).subscribe({
      next: (result) => {
        this.newEvents = (result.events || [])
          .map((e) => this._enrichEvent(e))
          .filter((e) => !this._endedBeforeToday(e))
          .slice(0, 5);
        this.newEventsLoading = false;
      },
      error: () => {
        this.newEventsLoading = false;
      },
    });
  }

  private _enrichEvent(event: any): any {
    if (event.start_datetime) {
      const date = this._parseDatetimeInTimezone(event.start_datetime, event.timezone || "America/Chicago");
      event._formattedDate = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    }
    return event;
  }

  private _endedBeforeToday(event: any): boolean {
    if (!event.start_datetime) return false;
    const tz = event.timezone || "America/Chicago";
    const endDt = event.end_datetime || event.start_datetime;
    const endTime = this._parseDatetimeInTimezone(endDt, tz);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return endTime < startOfToday;
  }

  private _parseDatetimeInTimezone(dt: string, timezone: string): Date {
    const parts = dt.split("T");
    const [y, mo, d] = parts[0].split("-").map(Number);
    const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
    const fake = new Date(y!, mo! - 1, d!, h!, mi!);
    const inTz = new Date(fake.toLocaleString("en-US", { timeZone: timezone }));
    const offset = fake.getTime() - inTz.getTime();
    return new Date(fake.getTime() + offset);
  }

  private _pickRandomBackgroundImage(): string {
    const images = [
      "/assets/images/hero/fui-hero3.jpg",
      "/assets/images/hero/fui-hero4.jpg",
      "/assets/images/hero/fui-hero5.jpg",
      "/assets/images/hero/fui-hero6.jpg",
      "/assets/images/hero/fui-hero10.jpg",
      "/assets/images/hero/fui-hero11.jpg",
      "/assets/images/hero/fui-hero12.jpg",
      "/assets/images/hero/fui-hero13.jpg",
      "/assets/images/hero/fui-hero14.jpg",
    ];
    return images[Math.floor(Math.random() * images.length)];
  }
}
